import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Zap, Heart, Video, ChevronRight, Sparkles } from "lucide-react";

/**
 * DesktopRightRail — contextual content rail shown on xl+ (≥1280px) only.
 *
 * Purpose: fill the right side of the laptop screen with genuinely useful
 * widgets so the main column (Discover swipe deck, Profile, etc.) no longer
 * looks "stranded" with empty space around it.
 *
 * Widgets:
 *   • Tonight in your city  — live beacon promo
 *   • Latest moments        — 3 most recent moments (thumbnails)
 *   • Recent reactions      — "X people liked you" upgrade nudge if free
 *
 * Hidden on:
 *   • Mobile/tablet (<1280px)        – not enough room
 *   • Chat workspace                 – conversation already occupies the right side
 *   • Auth / landing / immersive flows
 */
const HIDE_PATTERNS = [
  /^\/chats(\/.*)?$/, // chat workspace owns right column
  /^\/groups\/[^/]+$/, // open group conversation
  /^\/friends\/[^/]+$/, // open friend (1:1) conversation
  /^\/moments\/new$/,
  /^\/onboarding$/,
  /^\/verify-email$/,
  /^\/verify$/,
  /^\/login$/,
  /^\/signup$/,
  /^\/register$/,
  /^\/$/,
  /^\/v\d$/,
  /^\/demo$/,
  /^\/share/,
  /^\/pick$/,
];

function Widget({ title, icon: Icon, children, accent = "#C9A961" }) {
  return (
    <div
      className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm overflow-hidden"
      style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)" }}
    >
      <div className="flex items-center gap-2 px-4 pt-3.5 pb-2">
        <Icon className="w-3.5 h-3.5" strokeWidth={1.8} style={{ color: accent }} />
        <span
          className="text-[10px] uppercase tracking-[0.22em] font-medium"
          style={{ color: accent }}
        >
          {title}
        </span>
      </div>
      {children}
    </div>
  );
}

export default function DesktopRightRail() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  const [tonight, setTonight] = useState(null);
  const [moments, setMoments] = useState([]);
  const [teaser, setTeaser] = useState(null);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get("/tonight/count").catch(() => ({ data: { count: 0 } })),
      api.get("/moments/feed").catch(() => ({ data: [] })),
      api.get("/likes/teaser").catch(() => ({ data: null })),
    ]).then(([tRes, mRes, lRes]) => {
      setTonight(tRes.data || null);
      const items = Array.isArray(mRes.data) ? mRes.data : (mRes.data?.items || []);
      setMoments(items.slice(0, 3));
      setTeaser(lRes.data || null);
    });
  }, [user, location.pathname]);

  if (!user) return null;
  if (HIDE_PATTERNS.some((rx) => rx.test(location.pathname))) return null;

  const tonightCount =
    (tonight?.count != null ? tonight.count : null) ??
    (Array.isArray(tonight?.beacons) ? tonight.beacons.length : 0);
  const city = tonight?.city;
  const isPremium = !!(user.premium_until && new Date(user.premium_until) > new Date());

  return (
    <aside
      data-testid="desktop-right-rail"
      className="hidden xl:flex fixed right-0 top-0 bottom-0 z-20 w-[300px] flex-col gap-4 overflow-y-auto p-5 border-l border-white/[0.05] bg-[#0F1012]/60 backdrop-blur-xl"
    >
      {/* Header */}
      <div className="pt-1">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#6E6A62]">
          Tonight on Spark
        </div>
        <h3
          className="text-[22px] tracking-[-0.02em] text-[#F0E6D2] mt-0.5"
          style={{ fontFamily: '"Fraunces", "Cormorant Garamond", serif', fontWeight: 400 }}
        >
          What's happening
        </h3>
      </div>

      {/* Tonight beacons widget */}
      <Widget title="Tonight beacons" icon={Zap} accent="#C9A961">
        <Link
          to="/tonight"
          data-testid="rail-tonight"
          className="block px-4 pb-4 pt-1 hover:bg-white/[0.02] transition-colors"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
              style={{
                background: "radial-gradient(60% 60% at 50% 40%, #C9A961, #8E6F3A)",
                boxShadow: "0 0 18px rgba(201,169,97,0.35)",
              }}
            >
              <Zap className="w-5 h-5 text-[#16171A]" fill="currentColor" />
            </div>
            <div className="min-w-0">
              <div className="text-[14px] text-[#F0E6D2] leading-tight">
                {tonightCount > 0
                  ? `${tonightCount} ${tonightCount === 1 ? "person" : "people"} free${city ? ` in ${city}` : ""}`
                  : "Be the first to light it up"}
              </div>
              <div className="text-[11px] text-[#8C867C] mt-0.5">
                Quiet beacons · auto-expire in 12h
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-[#8C867C] ml-auto flex-shrink-0" />
          </div>
        </Link>
      </Widget>

      {/* Latest moments widget */}
      {moments.length > 0 && (
        <Widget title="Fresh moments" icon={Video} accent="#8FE0C5">
          <div className="px-4 pb-4 pt-1 grid grid-cols-3 gap-2">
            {moments.map((m) => (
              <Link
                key={m.id}
                to="/moments"
                data-testid={`rail-moment-${m.id}`}
                className="relative aspect-[3/4] rounded-lg overflow-hidden bg-[#25252A] group"
              >
                {m.thumbnail_path || m.storage_path ? (
                  <video
                    src={fileUrl(m.storage_path)}
                    className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                    muted
                    playsInline
                    preload="metadata"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#C9A961]/20 to-[#B95F73]/20" />
                )}
                <div className="absolute inset-x-0 bottom-0 px-1.5 py-1 bg-gradient-to-t from-black/70 to-transparent">
                  <span className="text-[9px] uppercase tracking-wider text-[#F0E6D2] truncate block">
                    {m.user?.name?.split(" ")[0] || "—"}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </Widget>
      )}

      {/* Reactions / upgrade nudge */}
      {teaser?.likes_count > 0 && (
        <Widget
          title={isPremium ? "Recent reactions" : "Spark+ unlock"}
          icon={isPremium ? Heart : Sparkles}
          accent={isPremium ? "#B95F73" : "#C9A961"}
        >
          <Link
            to={isPremium ? "/likes" : "/upgrade"}
            data-testid="rail-likes"
            className="block px-4 pb-4 pt-1 hover:bg-white/[0.02] transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2 flex-shrink-0">
                {(teaser.preview_avatars || []).slice(0, 3).map((p, i) => (
                  <div
                    key={i}
                    className="w-9 h-9 rounded-full overflow-hidden border-2 border-[#0F1012]"
                  >
                    {p.photo ? (
                      <img
                        src={fileUrl(p.photo, 320)}
                        alt=""
                        className="w-full h-full object-cover"
                        style={
                          isPremium
                            ? undefined
                            : { filter: "blur(7px) saturate(0.7)", transform: "scale(1.2)" }
                        }
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#C9A961]/50 to-[#8E6F3A]/50" />
                    )}
                  </div>
                ))}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[14px] text-[#F0E6D2] leading-tight">
                  {teaser.likes_count} {teaser.likes_count === 1 ? "person" : "people"} liked you
                </div>
                <div className="text-[11px] text-[#8C867C] mt-0.5">
                  {isPremium ? "See who" : "Unlock with Spark+"}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-[#8C867C] flex-shrink-0" />
            </div>
          </Link>
        </Widget>
      )}

      {/* Footer / brand quote */}
      <div className="mt-auto pt-4 border-t border-white/[0.04]">
        <p
          className="text-[12px] text-[#8C867C] leading-relaxed italic"
          style={{ fontFamily: '"Fraunces", serif' }}
        >
          "Quiet beacons. Real plans. No bios."
        </p>
        <div className="text-[9px] uppercase tracking-[0.28em] text-[#5C5750] mt-2">
          Spark — Editorial Dating
        </div>
      </div>
    </aside>
  );
}
